import { Box, Inline, xcss } from '@atlaskit/primitives';
import EntryLink from '@databases/content-link/EntryLink';
import { DatabaseFieldType } from '@databases/types/database';
import { MultiValueChangeType } from '@databases/utils/database/changesets';
import React, { type FC, useCallback } from 'react';

import { FieldValueRemoveButton, filterOutRemovedValue } from '../popover-edit-common';
import { type EditComponentProps } from '../types';

import type { EntryLinkReference } from './types';

interface EntryLinkValueProps extends EditComponentProps<DatabaseFieldType.ENTRY_LINK> {
  entryLinkValue: EntryLinkReference;
  contentId: string;
  isReadOnly: boolean;
  autoFocus?: boolean;
  addDataSectionRef: React.RefObject<HTMLDivElement>;
}

const preventDefault = (e: React.MouseEvent | React.FocusEvent) => {
  // Prevent propagation to inline edit
  e.stopPropagation();
};

export const EntryLinkValue: FC<EntryLinkValueProps> = ({
  entryLinkValue,
  contentId,
  isReadOnly,
  autoFocus,
  value: entryValues,
  addDataSectionRef,
  onChange
}) => {
  const linkValueIndex = entryValues.findIndex(
    (value) => entryLinkValue._id !== '' && value._id === entryLinkValue._id
  );
  const remainingValues = filterOutRemovedValue({
    currentValues: entryValues,
    indexToRemove: linkValueIndex
  });

  const onDeleteClick = useCallback(() => {
    onChange(remainingValues, {
      type: DatabaseFieldType.ENTRY_LINK,
      delta: {
        type: MultiValueChangeType.REMOVE,
        index: linkValueIndex
      }
    });

    if (addDataSectionRef.current) {
      addDataSectionRef.current?.getElementsByTagName('input')[0].focus();
    }
  }, [addDataSectionRef, linkValueIndex, onChange, remainingValues]);

  return (
    <Inline
      xcss={xcss({
        display: 'inline-flex',
        maxWidth: '100%',
        alignItems: 'center',
        justifyContent: 'space-between'
      })}
    >
      <Box xcss={xcss({ width: '90%' })}>
        <EntryLink
          data-testid="entry-link-value"
          entryRef={entryLinkValue}
          onClick={preventDefault}
          onFocus={preventDefault}
          contentId={contentId}
          isReadonly={isReadOnly}
        />
      </Box>
      <FieldValueRemoveButton
        testId={'entry-link-remove-button'}
        onItemDelete={onDeleteClick}
        autoFocus={autoFocus}
      />
    </Inline>
  );
};
